import { Col, Input, Modal, Row, Spin, Table, message } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import BreadcrumbStatus from "./BreadcrumbStatus";
import KpiHistoryBadge from "./KpiHistoryBadge";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import COLORS from "common/Colors";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import {
  KPI_PLANNING_MINE_STATUS,
  KPI_PLANNING_MYTEAM_STATUS,
  statusMeta,
} from "./statusTag";
import AdjustDatesModal from "./AdjustDatesModal";

const Wrap = styled.div`
  .breadcrumb {
    display: block;
  }
  .perspective-title {
    font-weight: 600;
    margin: 16px 0 8px;
  }
  .footer {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    padding: 10px;
    display: flex;
    justify-content: end;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin: 20px -20px 0;
  }
  .ant-table-thead > tr > th {
    background: #C0CDE2 !important;
    font-weight: 600;
    font-size: 13px;
    color: #000000;
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    padding: 8px 12px;
  }
  .planning-group-row > td {
    background: #fafafa;
  }
  .reason-card {
    background: #fafafa;
    border-radius: 8px;
    padding: 16px 20px;
    margin: 16px 0;

    .reason-card-title {
      color: #8c8c8c;
      margin-bottom: 8px;
    }
  }
`;

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

// The 4 perspectives render as ONE continuous table (one shared sticky
// header) - perspective titles are injected as full-width rows within the
// same table via colSpan, rather than each perspective having its own
// separate Table/header.
const buildPlanningRows = (grouped) => {
  const rows = [];
  Object.entries(grouped).forEach(([perspective, items]) => {
    rows.push({ key: `group-${perspective}`, isGroupHeader: true, perspective });
    items.forEach((row, idx) => rows.push({ ...row, key: `${perspective}-${idx}` }));
  });
  return rows;
};

const fullSpanCell = (record) => (record.isGroupHeader ? { colSpan: 5 } : {});
const hiddenCell = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});

const perspectiveColumns = [
  {
    title: "S.No",
    key: "sno",
    width: 80,
    onCell: fullSpanCell,
    render: (_, record) =>
      record.isGroupHeader ? (
        <div className="perspective-title m-0">{record.perspective}</div>
      ) : (
        record.sno
      ),
  },
  {
    title: "Measurement Dimensions",
    dataIndex: "measurementdimension",
    key: "measurementdimension",
    onCell: hiddenCell,
  },
  { title: "Objectives", dataIndex: "objectives", key: "objectives", onCell: hiddenCell },
  { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 140, onCell: hiddenCell },
  {
    title: "Planning Assumptions / Comments",
    dataIndex: "comments",
    key: "comments",
    onCell: hiddenCell,
  },
];

const rolloverColumns = [
  { title: "S.No", dataIndex: "sno", key: "sno", width: 80 },
  { title: "Objectives", dataIndex: "objectives", key: "objectives" },
  { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 140 },
  { title: "Planning Assumptions / Comments", dataIndex: "comments", key: "comments" },
];

const softSkillsColumns = [
  { title: "Objectives", dataIndex: "objectives", key: "objectives" },
  { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 140 },
];

const trainingColumns = [
  { title: "S.No", dataIndex: "sno", key: "sno", width: 80 },
  { title: "Skill to be developed / strengthened", dataIndex: "skill", key: "skill" },
  { title: "Method of development, i.e. whether on-the-job , formal training or other.", dataIndex: "method", key: "method" },
  { title: "Planned / Expected quarter", dataIndex: "quarter", key: "quarter", width: 160 },
];

const groupByPerspective = (perspectives = []) => {
  const grouped = {};
  perspectives.forEach((item) => {
    const key = item.perspective || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return grouped;
};

const ReviewContract = () => {
  const history = useHistory();
  const location = useLocation();
  const contractid = location.state?.contractid;
  const viewOnly = Boolean(location.state?.viewOnly);

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  // Holds which action is in flight ("accept" | "decline" | null) rather than
  // a single boolean, so only the button the user actually clicked spins.
  const [actionLoading, setActionLoading] = useState(null);
  const [popup, setPopup] = useState({ open: false, type: "success", message: "" });
  const [adjustDatesOpen, setAdjustDatesOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const onAdjustDate = () => {
    setAdjustDatesOpen(true);
  };

  const fetchContract = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/contract/${contractid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setContract(data);
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contractid) fetchContract();
  }, [contractid]);

  const respond = async (action, reason = "") => {
    setActionLoading(action);
    try {
      const { code } = await CALL_API(
        `performance-kpi-management/${action}/${contractid}`,
        "patch",
        action === "decline" ? { declinereason: reason } : {}
      );
      if (code === STATUS_CODE.SUCCESS) {
        setDeclineOpen(false);
        setDeclineReason("");
        setPopup({
          open: true,
          type: action === "accept" ? "success" : "decline",
          message: action === "accept" ? "Accepted" : "Declined",
        });
        setTimeout(() => {
          setPopup((prev) => ({ ...prev, open: false }));
          history.push("/performance", { id: "kpiplanning", subTab: "mine" });
        }, 1500);
      }
    } catch (error) {
      console.error(`Error trying to ${action} contract:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  const onAccept = () => {
    Modal.confirm({
      title: "Remember, this can't be changed. Are you sure you want to accept this contract?",
      icon: null,
      centered: true,
      okText: "Yes, Accept",
      cancelText: "No, Cancel",
      okButtonProps: { type: "primary" },
      onOk: () => respond("accept"),
    });
  };

  const onDecline = () => {
    setDeclineReason("");
    setDeclineOpen(true);
  };

  const onEdit = () => {
    history.push("/performance-kpi-create-contract", { contractid });
  };

  const onDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this KPI Planning?",
      icon: null,
      centered: true,
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        try {
          const { code, message: apiMessage } = await CALL_API(
            `performance-kpi-management/contract/${contractid}`,
            "delete"
          );
          if (code === STATUS_CODE.SUCCESS) {
            history.push("/performance", { id: "kpiplanning", subTab: "myteam" });
          } else {
            message.error(apiMessage || "Unable to delete this KPI Planning");
          }
        } catch (error) {
          console.error("Error deleting contract:", error);
          message.error("Unable to delete this KPI Planning");
        }
      },
    });
  };

  const grouped = groupByPerspective(contract?.perspectives);
  // Accept/Decline are the appraisee's own actions on their contract - a
  // manager opening the same "yettoaccept" contract from My Team is just
  // viewing it, not responding to it.
  const canRespond = !viewOnly && contract?.planningstatus === "yettoaccept";

  useEffect(() => {
    // The sticky footer is clamped to the padding edge of this scroll
    // container, so the layout's own bottom padding always shows as a gap
    // below it - drop it to 0 while it's rendered. When there's no footer
    // (viewOnly, or any status other than "yettoaccept"), leave the padding
    // alone or the page has no room to scroll past its last section.
    if (!canRespond) return;
    const main = document.querySelector(".ant-layout-content");
    if (!main) return;
    const previousPaddingBottom = main.style.paddingBottom;
    main.style.paddingBottom = "0";
    return () => {
      main.style.paddingBottom = previousPaddingBottom;
    };
  }, [canRespond]);

  const statusMap = viewOnly ? KPI_PLANNING_MYTEAM_STATUS : KPI_PLANNING_MINE_STATUS;
  const statusInfo = statusMeta(statusMap, contract?.planningstatus);

  return (
    <Wrap>
      <Spin spinning={loading}>
        <BreadcrumbStatus
          items={[
            {
              label: "Performance Management",
              onClick: () => history.push("/performance", { id: "kpiplanning", subTab: viewOnly ? "myteam" : "mine" }),
            },
            { label: viewOnly ? contract?.fullname || "Reviewing" : "Reviewing" },
          ]}
          status={contract ? statusInfo.label : null}
          actions={
            viewOnly &&
            contract && (
              <div className="d-flex align-items-center" style={{ gap: 24 }}>
                {/* editcontract always resets planningstatus back to
                    "yettoaccept" - only offer it for statuses where that's
                    actually correct (never responded to yet, or declined).
                    Anything further along (KPI review, progress review,
                    closed) would have its whole cycle wiped by this. */}
                {statusInfo.label === "Accepted" ? (
                  <span
                    style={{ color: COLORS.PRIMARY, cursor: "pointer" }}
                    onClick={onAdjustDate}
                  >
                    <EditOutlined /> Adjust Date
                  </span>
                ) : statusInfo.label === "Declined" ? (
                  <span style={{ color: COLORS.PRIMARY, cursor: "pointer" }} onClick={onEdit}>
                    <EditOutlined /> Edit & resend
                  </span>
                ) : contract?.planningstatus === "yettoaccept" ? (
                  <span style={{ color: COLORS.PRIMARY, cursor: "pointer" }} onClick={onEdit}>
                    <EditOutlined /> Edit
                  </span>
                ) : null}
                <span style={{ color: COLORS.ERROR, cursor: "pointer" }} onClick={onDelete}>
                  <DeleteOutlined /> Delete
                </span>
              </div>
            )
          }
        />

        {/* <h6>
          {contract?.period || "Yearly"} Appraisal –{" "}
          <span className="lite-text">Key responsibilities / targets for the appraisal</span>
        </h6> */}

        {viewOnly && (
          <Row gutter={[16, 16]} className="my-3">
            <Col xs={24} md={8}>
              <div className="lite-text">Name of the employee</div>
              <div className="text-bold">{contract?.fullname || "-"}</div>
            </Col>
            <Col xs={24} md={8}>
              <div className="lite-text">Period</div>
              <div className="text-bold">{contract?.period || "-"}</div>
            </Col>
          </Row>
        )}

        <Row gutter={[16, 16]} className="my-3">
          <Col xs={24} md={8}>
            <div className="lite-text">Start date to attend appraisal</div>
            <div className="text-bold">{formatDate(contract?.startdate)}</div>
          </Col>
          <Col xs={24} md={8}>
            <div className="lite-text">Progress Review</div>
            <div className="text-bold">
              {(contract?.progressreviewdates || []).length > 0 ? (
                contract.progressreviewdates.map((item, idx) => (
                  <div key={idx}>
                    {item.label} – {formatDate(item.date)}
                  </div>
                ))
              ) : (
                "No"
              )}
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="lite-text">KPI defined by</div>
            <div className="text-bold">
              {contract?.kpidefinedby === "Self"
                ? `Appraisee : ${contract?.fullname || "-"}`
                : `Appraiser : ${contract?.kpidefinedby || "-"}`}
            </div>
          </Col>
        </Row>

        {["declined", "revisionrequested"].includes(contract?.planningstatus) && contract?.declinereason && (
          <div className="reason-card">
            <div className="reason-card-title">
              {contract.planningstatus === "revisionrequested"
                ? "Reason for revision request"
                : "Reason for decline"}
            </div>
            <div>{contract.declinereason}</div>
          </div>
        )}

        {contract?.id && statusInfo.label !== "Yet to Accept" && <KpiHistoryBadge contractid={contract.id} />}

        <h6 className="mt-4">Planning</h6>
        <p className="lite-text">
          Describe specific Objectives or other developmental activities to be
          undertaken by Appraisee during the appraisal period.
        </p>

        <Table
          columns={perspectiveColumns}
          dataSource={buildPlanningRows(grouped)}
          pagination={false}
          rowKey={(row) => row.key}
          rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
        />

        {contract?.rolloverresponsibilities?.length > 0 && (
          <>
            <div className="perspective-title">Rollover Responsibility from Previous Appraisal</div>
            <Table
              columns={rolloverColumns}
              dataSource={contract.rolloverresponsibilities}
              pagination={false}
              rowKey={(row, idx) => `rollover-${idx}`}
            />
          </>
        )}

        {contract?.softskillsassessment?.length > 0 && (
          <>
            <div className="perspective-title">Individual (Soft Skills) Assessment</div>
            <Table
              columns={softSkillsColumns}
              dataSource={contract.softskillsassessment}
              pagination={false}
              rowKey={(row, idx) => `softskills-${idx}`}
            />
          </>
        )}

        {contract?.trainingdevelopmentplan?.length > 0 && (
          <>
            <div className="perspective-title">Training & Development Plan</div>
            <p className="lite-text">
              Based on the strengths and limitations identified in the previous review, indicate the
              work experience and/or training suggested during the plan period.
            </p>
            <Table
              columns={trainingColumns}
              dataSource={contract.trainingdevelopmentplan}
              pagination={false}
              rowKey={(row, idx) => `training-${idx}`}
            />
          </>
        )}

        {contract?.appraiseecomment && (
          <>
            {/* Same underlying field either way - whoever defined the KPI
                (appraiser or the appraisee themself) is who actually wrote
                this comment, so the label has to match kpidefinedby rather
                than always claiming it's the appraisee's own. */}
            <div className="perspective-title">
              {contract?.kpidefinedby === "Self" ? "Appraisee's Comment" : "Appraiser's Comment"}
            </div>
            <p>{contract.appraiseecomment}</p>
          </>
        )}

        {canRespond && (
          <div className="footer">
            {/* Decline only opens the reason modal (the API call happens from
                the modal), so it never shows a spinner itself - it is just
                disabled while any request is in flight. */}
            <ButtonOutlined disabled={Boolean(actionLoading)} onClick={onDecline}>
              Decline
            </ButtonOutlined>
            <ButtonFilled
              type="primary"
              loading={actionLoading === "accept"}
              disabled={actionLoading === "decline"}
              onClick={onAccept}
            >
              Accept
            </ButtonFilled>
          </div>
        )}
      </Spin>

      <AdjustDatesModal
        open={adjustDatesOpen}
        onClose={() => setAdjustDatesOpen(false)}
        record={contract}
        onSuccess={fetchContract}
      />
      <Modal
        open={declineOpen}
        onCancel={() => setDeclineOpen(false)}
        centered
        closable={false}
        title={null}
        footer={null}
        width={520}
      >
        <div className="lite-text mb-2">Reason for Decline</div>
        <Input.TextArea
          rows={5}
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
          placeholder="Enter your reason"
        />
        <div className="d-flex justify-content-end mt-4" style={{ gap: 12 }}>
          <ButtonOutlined
            disabled={actionLoading === "decline"}
            onClick={() => setDeclineOpen(false)}
          >
            Cancel
          </ButtonOutlined>
          <ButtonFilled
            type="primary"
            loading={actionLoading === "decline"}
            disabled={!declineReason.trim()}
            onClick={() => respond("decline", declineReason.trim())}
          >
            Decline
          </ButtonFilled>
        </div>
      </Modal>
      <SuccessDeclinePopup
        open={popup.open}
        setOpen={(open) => setPopup((prev) => ({ ...prev, open }))}
        type={popup.type}
        message={popup.message}
      />
    </Wrap>
  );
};

export default ReviewContract;